<template>
  <b-modal
    class="mc-modal"
    id="mc-modal-apply-template"
    ref="mc-modal-apply-template"
    :title="$t('costManagement.template.header')"
    size="lg"
    centered
    @shown="handleModalOpen"
    @hidden="handleModalClose"
  >
    <div class="card-body p-2">
      <b-container fluid>
        <b-row>
          <b-col cols="5" class="border p-3 mc-template-list" @scroll="handleListScroll">
            <p>{{ $t('costManagement.template.select') }}</p>
            <div class="mc-template-search__wrapper mb-3">
              <b-input-group size="sm">
                <template #prepend>
                  <b-input-group-text>
                    <i class="icon-outline_search"></i>
                  </b-input-group-text>
                </template>
                <b-form-input
                  type="search"
                  :placeholder="`${$t('global.search')}...`"
                  @input="handleSearchChange"
                  :value="searchTemplate"
                />
              </b-input-group>
            </div>
            <div class="w-100 mc-template-select">
              <b-tabs pills vertical content-class="w-100" v-model="selectedIndex">
                <b-tab v-for="(template, index) in templates" :key="template.id">
                  <template #title>
                    <div class="d-flex justify-content-between">
                      <span>{{ template.name }}</span>
                      <span v-if="selectedIndex == index" class="check-icon">
                        <i class="mc-icon fas fa-check-circle"></i>
                      </span>
                    </div>
                  </template>
                </b-tab>
              </b-tabs>
              <div v-if="listLoading" class="d-flex justify-content-center mt-2">
                <b-spinner />
              </div>
            </div>
          </b-col>
          <b-col cols="7" class="border p-3 mc-template-detail">
            <div class="mb-4" v-if="templateDetail">
              <p class="h5 mb-0 mc-general__label">{{ templateDetail.title }}</p>
            </div>
            <div class="table-responsive">
              <b-table
                :items="templateDetail?.details ?? []"
                :fields="[
                  { key: 'label', label: $t('costManagement.fields.costCategoryName') },
                  { key: 'amount', label: $t('costManagement.fields.cost') },
                ]"
                fixed
                show-empty
                class="editable-table"
              >
                <!-- Empty Result -->
                <template #empty>
                  <div class="text-center my-4">{{ $t('global.text.noData') }}</div>
                </template>
              </b-table>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </div>
    <template #modal-footer="{ cancel }">
      <div class="d-flex">
        <McButton
          variant="secondary"
          class="me-2"
          :text="$t('global.action.cancel')"
          @click="cancel"
        />
        <McButton
          variant="primary"
          :text="$t('global.action.apply')"
          :disabled="applyBtnDisabled"
          @click="applyTemplate(cancel)"
        />
      </div>
    </template>
  </b-modal>
</template>

<script>
import { debounce } from '@/mixin/helper'
import * as Template from '@/services/rest-api/lib/cost-management/template'
import McButton from '@/components/McButton'
import { APPLY_TEMPLATE_FETCH_COUNT } from '../../lib/constants/template'
import messages from '../../lib/constants/error-message-mapper'

import moment from 'moment'
moment.locale('id')

export default {
  components: {
    McButton,
  },
  props: {},
  data() {
    return {
      templates: [],
      templateDetail: null,
      selectedIndex: 0,
      listLoading: false,
      detailLoading: false,
      searchTemplate: '',
      listFetchPage: 1,
      pageLimit: 1,
    }
  },
  computed: {
    applyBtnDisabled() {
      const details = this.templateDetail?.details ?? []
      return this.detailLoading || details.length === 0
    },
  },
  watch: {
    selectedIndex: debounce(function () {
      this.refreshDetail()
    }, 50),
  },
  methods: {
    async loadMoreTemplates() {
      try {
        this.listLoading = true
        const res = await Template.getList(
          this.listFetchPage,
          APPLY_TEMPLATE_FETCH_COUNT,
          this.searchTemplate,
          'name asc'
        )

        const newTemplates = res.data.paginated_result.map((result) => ({
          id: result.id,
          name: result.name,
        }))
        this.templates = this.templates.concat(newTemplates)
        this.pageLimit = res.metadata.total_page
      } catch (e) {
        this.$mcToast.error('Terjadi kesalahan.')
      } finally {
        this.selectedIndex = 0
        this.listLoading = false
        this.refreshDetail()
      }
    },
    async handleModalOpen() {
      this.loadMoreTemplates()
    },
    handleModalClose() {
      this.templates = []
      this.selectedIndex = 0
      this.templateDetail = null
      this.searchTemplate = ''
      this.pageLimit = 1
      this.listFetchPage = 1
    },
    applyTemplate(closeCallback) {
      this.$emit('apply-template', this.templateDetail.id)

      closeCallback()
    },
    handleSearchChange: debounce(function (_search) {
      this.searchTemplate = _search
      this.pageLimit = 1
      this.listFetchPage = 1
      this.templates = []

      this.loadMoreTemplates()
    }, 500),
    async refreshDetail() {
      try {
        if (this.templates.length === 0) {
          this.templateDetail = null
          return
        }

        this.detailLoading = true

        const selectedId = this.templates[this.selectedIndex].id
        const res = await Template.show(selectedId)

        if (res.errors) {
          const errorMsg = messages[res.message] ?? 'Terjadi kesalahan.'
          return this.$mcToast.error(errorMsg)
        }

        this.templateDetail = {
          id: selectedId,
          title: this.templates[this.selectedIndex].name,
          details: res.data.details.map((d) => ({
            id: d.id,
            label: d.activity.name,
            amount: d.amount.toLocaleString('en-US'),
          })),
        }
      } catch (e) {
        this.$mcToast.error('Terjadi kesalahan.')
      } finally {
        this.detailLoading = false
      }
    },
    handleListScroll({ target: { scrollTop, clientHeight, scrollHeight } }) {
      const isBottom = scrollTop + clientHeight >= scrollHeight

      if (isBottom && !this.listLoading && this.listFetchPage < this.pageLimit) {
        this.listFetchPage++

        this.loadMoreTemplates()
      }
    },
  },
}
</script>

<style lang="scss">
@import './style.scss';
</style>
